import * as XLSX from 'xlsx';

interface ReferenceInstructor {
  id: number;
  name: string;
  code?: string;
  firstName?: string;
  lastName?: string;
}

interface ReferenceTopic {
  id: number;
  name: string;
  code?: string;
  topicCode?: string;
}

interface ReferenceBatch {
  batchId: number;
  batchName?: string;
  batchCode?: string;
}

/**
 * Generate and download an Excel template for date-specific slot import
 */
export function generateSlotsTemplate(
  instructors: ReferenceInstructor[],
  topics: ReferenceTopic[],
  batches?: ReferenceBatch[]
): void {
  const wb = XLSX.utils.book_new();

  // Sheet 1: Slots template with headers and sample rows
  const slotsData = [
    ['Date', 'Start Time', 'End Time', 'Instructor ID', 'Instructor Name', 'Topic ID', 'Topic Name', 'Batches', 'Is Online', 'Agenda'],
    ['02-06-2025', '09:00', '10:00', 42, '', 15, '', 'BATCH_A,BATCH_B', 'true', 'Chapter 1 introduction'],
    ['02-06-2025', '10:00', '11:00', '', 'Dr. Smith', '', 'Algebra', 'BATCH_A', '', 'Review session'],
  ];
  const slotsSheet = XLSX.utils.aoa_to_sheet(slotsData);
  slotsSheet['!cols'] = [
    { wch: 12 }, // Date
    { wch: 12 }, // Start Time
    { wch: 12 }, // End Time
    { wch: 14 }, // Instructor ID
    { wch: 20 }, // Instructor Name
    { wch: 10 }, // Topic ID
    { wch: 20 }, // Topic Name
    { wch: 20 }, // Batches
    { wch: 10 }, // Is Online
    { wch: 30 }, // Agenda
  ];
  XLSX.utils.book_append_sheet(wb, slotsSheet, 'Slots');

  // Sheet 2: Instructors reference
  appendInstructorSheet(wb, instructors);

  // Sheet 3: Topics reference
  appendTopicSheet(wb, topics);

  // Sheet 4: Batches reference
  if (batches && batches.length > 0) {
    appendBatchSheet(wb, batches);
  }

  XLSX.writeFile(wb, 'slots-import-template.xlsx');
}

/**
 * Generate and download an Excel template for week pattern import
 */
export function generatePatternTemplate(
  instructors: ReferenceInstructor[],
  topics: ReferenceTopic[]
): void {
  const wb = XLSX.utils.book_new();

  // Sheet 1: Pattern template with headers and sample rows
  const patternData = [
    ['Day', 'Start Time', 'End Time', 'Instructor ID', 'Instructor Name', 'Topic ID', 'Topic Name', 'Agenda'],
    ['Mon', '09:00', '10:00', 42, '', 15, '', 'Weekly session'],
    ['Tue', '10:00', '11:00', '', 'Dr. Smith', '', 'Algebra', 'Lab work'],
    ['Wed', '09:00', '10:00', '', '', '', '', 'Free period'],
  ];
  const patternSheet = XLSX.utils.aoa_to_sheet(patternData);
  patternSheet['!cols'] = [
    { wch: 12 }, // Day
    { wch: 12 }, // Start Time
    { wch: 12 }, // End Time
    { wch: 14 }, // Instructor ID
    { wch: 20 }, // Instructor Name
    { wch: 10 }, // Topic ID
    { wch: 20 }, // Topic Name
    { wch: 30 }, // Agenda
  ];
  XLSX.utils.book_append_sheet(wb, patternSheet, 'Pattern');

  // Sheet 2: Instructors reference
  appendInstructorSheet(wb, instructors);

  // Sheet 3: Topics reference
  appendTopicSheet(wb, topics);

  XLSX.writeFile(wb, 'pattern-import-template.xlsx');
}

function appendInstructorSheet(wb: XLSX.WorkBook, instructors: ReferenceInstructor[]): void {
  const instructorRows: any[][] = [['ID', 'Name', 'Code']];
  for (const inst of instructors) {
    const displayName = inst.name || [inst.firstName, inst.lastName].filter(Boolean).join(' ');
    instructorRows.push([inst.id, displayName, inst.code || '']);
  }
  const instructorSheet = XLSX.utils.aoa_to_sheet(instructorRows);
  instructorSheet['!cols'] = [
    { wch: 8 },  // ID
    { wch: 30 }, // Name
    { wch: 15 }, // Code
  ];
  XLSX.utils.book_append_sheet(wb, instructorSheet, 'Reference - Instructors');
}

function appendTopicSheet(wb: XLSX.WorkBook, topics: ReferenceTopic[]): void {
  const topicRows: any[][] = [['ID', 'Name', 'Code']];
  for (const topic of topics) {
    topicRows.push([topic.id, topic.name || '', topic.code || topic.topicCode || '']);
  }
  const topicSheet = XLSX.utils.aoa_to_sheet(topicRows);
  topicSheet['!cols'] = [
    { wch: 8 },  // ID
    { wch: 30 }, // Name
    { wch: 15 }, // Code
  ];
  XLSX.utils.book_append_sheet(wb, topicSheet, 'Reference - Topics');
}

function appendBatchSheet(wb: XLSX.WorkBook, batches: ReferenceBatch[]): void {
  const batchRows: any[][] = [['ID', 'Name', 'Code']];
  for (const batch of batches) {
    batchRows.push([batch.batchId, batch.batchName || '', batch.batchCode || '']);
  }
  const batchSheet = XLSX.utils.aoa_to_sheet(batchRows);
  batchSheet['!cols'] = [
    { wch: 8 },  // ID
    { wch: 30 }, // Name
    { wch: 15 }, // Code
  ];
  XLSX.utils.book_append_sheet(wb, batchSheet, 'Reference - Batches');
}
